/**
 * Confirmation before a consequential action, in place of the browser's
 * own pop-up: titled, keyboard-friendly, and styled like the rest. Ask with
 * `const confirm = useConfirm(); if (await confirm(...)) ...`.
 */
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { buttonVariants } from "./button-variants";
import { type Ask, type ConfirmOptions, ConfirmContext } from "./use-confirm";

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [asked, setAsked] = useState<ConfirmOptions | null>(null);
  const answer = useRef<((value: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((options) => {
    setAsked(typeof options === "string" ? { title: options } : options);
    return new Promise<boolean>((resolve) => {
      answer.current = resolve;
    });
  }, []);

  const settle = (value: boolean) => {
    answer.current?.(value);
    answer.current = null;
    setAsked(null);
  };

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <AlertDialog.Root open={asked !== null} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-fade-in" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-lg border border-border bg-popover p-6 text-foreground shadow-xl data-[state=open]:animate-zoom-in">
            <AlertDialog.Title className="text-lg font-semibold leading-snug">{asked?.title}</AlertDialog.Title>
            {asked?.body ? (
              <AlertDialog.Description className="text-sm text-muted-foreground">{asked.body}</AlertDialog.Description>
            ) : (
              <AlertDialog.Description className="sr-only">{asked?.title}</AlertDialog.Description>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <AlertDialog.Cancel className={buttonVariants({ variant: "secondary" })}>{t("ui.cancel")}</AlertDialog.Cancel>
              <AlertDialog.Action
                className={cn(buttonVariants({ variant: asked?.destructive ? "destructive" : "primary" }))}
                onClick={() => { settle(true); }}
              >
                {asked?.confirmLabel ?? t("ui.confirm")}
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  );
} // prettier-ignore
